import { Box, Card, Group, Stack, Text, Title } from "@mantine/core";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  StageBucket,
  buildCPTTimeAxis,
  buildStageBuckets,
  stagePalette,
} from "../utils/cptStageMetrics";
import type {
  ContinuousPerformanceStageEvent,
  DistanceData,
  HeadVelocity,
  PupilSizeData,
} from "types/tests";

interface CPTEyeTrackingPanelProps {
  stageEvents?: ContinuousPerformanceStageEvent[];
  headVelocityData?: HeadVelocity[];
  eyeDisplacements?: DistanceData[];
  pupilSizeData?: PupilSizeData[];
}

const SVG_WIDTH = 1000;
const MARGIN = { top: 32, right: 24, bottom: 36, left: 72 };
const PANEL_HEIGHT = 140;
const PANEL_GAP = 28;

const LINE_STROKE_WIDTH = 2;
const LINE_COLOR = "blue-7";
const BAND_FILL_OPACITY = 0.18;

interface ChannelPoint {
  t: number;
  v: number;
}

interface BandPoint {
  t: number;
  lo: number;
  hi: number;
  mean: number;
}

interface Channel {
  key: string;
  label: string;
  unit: string;
  yDomain: [number, number];
  yTickValues: number[];
  yFormat: (v: number) => string;
  data: ChannelPoint[];
  band?: BandPoint[];
  noDataKey?: string;
  primaryColor?: string;
  secondaryData?: ChannelPoint[];
  secondaryColor?: string;
  bandColor?: string;
}

const EMPTY_DOMAIN: [number, number] = [0, 1];
const EMPTY_TICKS: number[] = [0, 0.5, 1];
const EMPTY_FORMAT = () => "";

function niceDomain(
  values: number[],
  opts: { padPct?: number; floorZero?: boolean } = {},
): [number, number] {
  if (!values.length) return [0, 1];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padPct = opts.padPct ?? 0.06;
  const span = Math.max(max - min, 1e-9);
  let lo = min - span * padPct;
  let hi = max + span * padPct;
  if (opts.floorZero) lo = Math.min(lo, 0);
  if (opts.floorZero && min >= 0) lo = 0;
  if (lo === hi) {
    lo -= 1;
    hi += 1;
  }
  return [lo, hi];
}

function evenlySpacedTicks(domain: [number, number], count: number): number[] {
  const [lo, hi] = domain;
  if (lo === hi) return [lo];
  const step = (hi - lo) / count;
  return Array.from({ length: count + 1 }, (_, i) => lo + i * step);
}

function ticksFromZero(maxValue: number, step: number): number[] {
  const top = Math.max(step, Math.ceil(maxValue / step) * step);
  const count = Math.round(top / step);
  return Array.from({ length: count + 1 }, (_, i) => i * step);
}

function pickScaleExponent(ticks: number[]): number | null {
  const maxAbs = Math.max(...ticks.map((v) => Math.abs(v)));
  if (!Number.isFinite(maxAbs) || maxAbs === 0) return null;
  if (maxAbs >= 0.1 && maxAbs < 1000) return null;
  return Math.floor(Math.log10(maxAbs));
}

function formatExponent(exp: number): string {
  const digits = "0123456789";
  const supers = "⁰¹²³⁴⁵⁶⁷⁸⁹";
  const sign = exp < 0 ? "⁻" : "";
  return (
    sign +
    Math.abs(exp)
      .toString()
      .split("")
      .map((d) => supers[digits.indexOf(d)] ?? d)
      .join("")
  );
}

function buildPupilChannel(samples: PupilSizeData[] | undefined, t: TFunction): Channel {
  const label = t("extraTests.continuousPerformance.result.eyeTracking.channel.pupilSize");
  const unit = t("extraTests.continuousPerformance.result.eyeTracking.unit.millimetres");
  const empty: Channel = {
    key: "pupil",
    label,
    unit,
    yDomain: EMPTY_DOMAIN,
    yTickValues: EMPTY_TICKS,
    yFormat: EMPTY_FORMAT,
    data: [],
    noDataKey: "extraTests.continuousPerformance.result.eyeTracking.channel.pupilSizeNoSession",
  };
  if (!samples?.length) return empty;
  const left: ChannelPoint[] = [];
  const right: ChannelPoint[] = [];
  const band: BandPoint[] = [];
  const allValues: number[] = [];
  for (const s of samples) {
    if (!Number.isFinite(s.timeStamp)) continue;
    const l = typeof s.leftPupilSize === "number" ? s.leftPupilSize : null;
    const r = typeof s.rightPupilSize === "number" ? s.rightPupilSize : null;
    if (l === null && r === null) continue;
    if (l !== null) {
      left.push({ t: s.timeStamp, v: l });
      allValues.push(l);
    }
    if (r !== null) {
      right.push({ t: s.timeStamp, v: r });
      allValues.push(r);
    }
    if (l !== null && r !== null) {
      band.push({
        t: s.timeStamp,
        lo: Math.min(l, r),
        hi: Math.max(l, r),
        mean: (l + r) / 2,
      });
    }
  }
  if (!left.length && !right.length) return empty;
  const maxPupil = allValues.reduce((m, v) => (v > m ? v : m), 0);
  const ticks = ticksFromZero(maxPupil, 0.5);
  const domain: [number, number] = [0, ticks[ticks.length - 1]];
  return {
    key: "pupil",
    label,
    unit,
    yDomain: domain,
    yTickValues: ticks,
    yFormat: (v) => v.toFixed(1),
    data: left,
    secondaryData: right,
    band,
    primaryColor: "orange-6",
    secondaryColor: "blue-6",
    bandColor: "gray-5",
  };
}

function buildHeadVelocityChannel(samples: HeadVelocity[] | undefined, t: TFunction): Channel {
  const label = t("extraTests.continuousPerformance.result.eyeTracking.channel.headVelocity");
  const baseUnit = t(
    "extraTests.continuousPerformance.result.eyeTracking.unit.centimetersPerSecond",
  );
  const empty: Channel = {
    key: "headVelocity",
    label,
    unit: baseUnit,
    yDomain: EMPTY_DOMAIN,
    yTickValues: EMPTY_TICKS,
    yFormat: EMPTY_FORMAT,
    data: [],
    noDataKey: "extraTests.continuousPerformance.result.eyeTracking.channel.headVelocityNoSession",
  };
  if (!samples?.length) return empty;
  const filtered = samples.filter(
    (s) =>
      typeof s.velocity === "number" && Number.isFinite(s.velocity) && Number.isFinite(s.timeStamp),
  );
  if (!filtered.length) return empty;
  const data = filtered.map((s) => ({ t: s.timeStamp, v: s.velocity * 100 }));
  const domain = niceDomain(
    data.map((p) => p.v),
    { floorZero: true },
  );
  const ticks = evenlySpacedTicks(domain, 3);
  const scaleExp = pickScaleExponent(ticks);
  const scaleFactor = scaleExp === null ? 1 : Math.pow(10, scaleExp);
  const unit = scaleExp === null ? baseUnit : `×10${formatExponent(scaleExp)} ${baseUnit}`;
  return {
    key: "headVelocity",
    label,
    unit,
    yDomain: domain,
    yTickValues: ticks,
    yFormat: (v) => (v / scaleFactor).toFixed(1),
    data,
  };
}

function buildEyeDisplacementChannel(samples: DistanceData[] | undefined, t: TFunction): Channel {
  const label = t("extraTests.continuousPerformance.result.eyeTracking.channel.eyeDisplacement");
  const unit = t("extraTests.continuousPerformance.result.eyeTracking.unit.percentOffCentre");
  const empty: Channel = {
    key: "eyeDisplacement",
    label,
    unit,
    yDomain: EMPTY_DOMAIN,
    yTickValues: EMPTY_TICKS,
    yFormat: EMPTY_FORMAT,
    data: [],
    noDataKey:
      "extraTests.continuousPerformance.result.eyeTracking.channel.eyeDisplacementNoSession",
  };
  if (!samples?.length) return empty;
  const filtered = samples.filter(
    (s) =>
      typeof s.distance === "number" && Number.isFinite(s.distance) && Number.isFinite(s.timeStamp),
  );
  if (!filtered.length) return empty;
  const maxDistance = filtered.reduce((m, s) => (s.distance > m ? s.distance : m), 0);
  const ticks = ticksFromZero(maxDistance, 10);
  const domain: [number, number] = [0, ticks[ticks.length - 1]];
  return {
    key: "eyeDisplacement",
    label,
    unit,
    yDomain: domain,
    yTickValues: ticks,
    yFormat: (v) => v.toFixed(0),
    data: filtered.map((s) => ({ t: s.timeStamp, v: s.distance })),
  };
}

function stageRoleTint(stripe: "a" | "b"): string {
  return stripe === "a" ? "var(--mantine-color-default-hover)" : "rgba(0,0,0,0)";
}

function stagePillStyles(stage: { role: StageBucket["role"]; rawName: string }) {
  const palette = stagePalette(stage);
  return { hue: palette.hue, shade: palette.intensity === "dark" ? 7 : 3 };
}

const CPTEyeTrackingPanel: React.FC<CPTEyeTrackingPanelProps> = ({
  stageEvents,
  headVelocityData,
  eyeDisplacements,
  pupilSizeData,
}) => {
  const { t } = useTranslation();
  const ns = "extraTests.continuousPerformance.result.eyeTracking";
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hover, setHover] = useState<{
    channelIndex: number;
    point: ChannelPoint;
    sublabel: string | null;
  } | null>(null);
  const channels = useMemo<Channel[]>(
    () => [
      buildHeadVelocityChannel(headVelocityData, t),
      buildEyeDisplacementChannel(eyeDisplacements, t),
      buildPupilChannel(pupilSizeData, t),
    ],
    [headVelocityData, eyeDisplacements, pupilSizeData, t],
  );

  const buckets = useMemo(() => buildStageBuckets(stageEvents), [stageEvents]);

  const dataMaxT = channels.reduce(
    (max, ch) => ch.data.reduce((m, p) => (p.t > m ? p.t : m), max),
    0,
  );
  const stageMaxT = buckets.reduce((max, b) => (b.endTime > max ? b.endTime : max), 0);
  const rawMaxT = Math.max(dataMaxT, stageMaxT, 1);
  const timeAxis = buildCPTTimeAxis(rawMaxT);
  const xTicks = timeAxis.ticks;
  const xLabels = timeAxis.labels;
  const xDomain: [number, number] = timeAxis.domain;

  const plotWidth = SVG_WIDTH - MARGIN.left - MARGIN.right;
  const channelStackHeight =
    channels.length * PANEL_HEIGHT + Math.max(0, channels.length - 1) * PANEL_GAP;
  const totalHeight = MARGIN.top + channelStackHeight + MARGIN.bottom;

  const scaleX = (t: number) => {
    const [lo, hi] = xDomain;
    const clamped = Math.max(lo, Math.min(hi, t));
    return MARGIN.left + ((clamped - lo) / (hi - lo)) * plotWidth;
  };

  const scaleYForChannel = (channelIndex: number, v: number): number => {
    const ch = channels[channelIndex];
    const [lo, hi] = ch.yDomain;
    const panelTop = MARGIN.top + channelIndex * (PANEL_HEIGHT + PANEL_GAP);
    const panelBottom = panelTop + PANEL_HEIGHT;
    if (hi === lo) return (panelTop + panelBottom) / 2;
    return panelBottom - ((v - lo) / (hi - lo)) * PANEL_HEIGHT;
  };

  const polylineFor = (channelIndex: number, points: ChannelPoint[]) =>
    points
      .map((p) => `${scaleX(p.t).toFixed(2)},${scaleYForChannel(channelIndex, p.v).toFixed(2)}`)
      .join(" ");

  const polylines = channels.map((ch, i) => polylineFor(i, ch.data));
  const secondaryPolylines = channels.map((ch, i) =>
    ch.secondaryData?.length ? polylineFor(i, ch.secondaryData) : null,
  );

  const bandPaths = channels.map((ch, i) => {
    if (!ch.band?.length) return null;
    const top = ch.band
      .map((p) => `${scaleX(p.t).toFixed(2)},${scaleYForChannel(i, p.hi).toFixed(2)}`)
      .join(" L ");
    const bottom = [...ch.band]
      .reverse()
      .map((p) => `${scaleX(p.t).toFixed(2)},${scaleYForChannel(i, p.lo).toFixed(2)}`)
      .join(" L ");
    return `M ${top} L ${bottom} Z`;
  });

  const handleHoverMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const rect = svgEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const localX = ((event.clientX - rect.left) / rect.width) * SVG_WIDTH;
    const localY = ((event.clientY - rect.top) / rect.height) * totalHeight;
    if (localX < MARGIN.left || localX > MARGIN.left + plotWidth) {
      setHover(null);
      return;
    }
    let channelIndex = -1;
    for (let i = 0; i < channels.length; i += 1) {
      const panelTop = MARGIN.top + i * (PANEL_HEIGHT + PANEL_GAP);
      if (localY >= panelTop && localY <= panelTop + PANEL_HEIGHT) {
        channelIndex = i;
        break;
      }
    }
    if (channelIndex === -1) {
      setHover(null);
      return;
    }
    const channel = channels[channelIndex];
    if (channel.noDataKey || !channel.data.length) {
      setHover(null);
      return;
    }
    let bestPoint: ChannelPoint | null = null;
    let bestSublabel: string | null = null;
    let bestDist = Infinity;
    const consider = (point: ChannelPoint, sublabel: string | null) => {
      const dx = scaleX(point.t) - localX;
      const dy = scaleYForChannel(channelIndex, point.v) - localY;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        bestPoint = point;
        bestSublabel = sublabel;
      }
    };
    const hasSecondary = !!channel.secondaryData?.length;
    const primaryLabel = hasSecondary ? t(`${ns}.legend.pupilLeft`) : null;
    for (const point of channel.data) consider(point, primaryLabel);
    if (hasSecondary && channel.secondaryData) {
      const secondaryLabel = t(`${ns}.legend.pupilRight`);
      for (const point of channel.secondaryData) consider(point, secondaryLabel);
    }
    if (bestPoint) setHover({ channelIndex, point: bestPoint, sublabel: bestSublabel });
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={5}>{t(`${ns}.panelTitle`)}</Title>
          <Text size="sm" c="dimmed" lh={1.4}>
            {t(`${ns}.panelDescription`)}
          </Text>
        </Stack>

        <Box style={{ overflowX: "auto" }}>
          <svg
            ref={svgRef}
            role="img"
            aria-label={t(`${ns}.panelTitle`)}
            viewBox={`0 0 ${SVG_WIDTH} ${totalHeight}`}
            width="100%"
            style={{ display: "block", minWidth: 640 }}
            onMouseMove={handleHoverMove}
            onMouseLeave={() => setHover(null)}
          >
            {buckets.map((bucket, idx) => {
              const x1 = scaleX(bucket.startTime);
              const x2 = scaleX(bucket.endTime);
              if (x2 <= x1) return null;
              const fill = stageRoleTint(idx % 2 === 0 ? "a" : "b");
              return (
                <g key={`band-${bucket.key}`}>
                  {channels.map((_, i) => (
                    <rect
                      key={`band-${bucket.key}-${i}`}
                      x={x1}
                      y={MARGIN.top + i * (PANEL_HEIGHT + PANEL_GAP)}
                      width={x2 - x1}
                      height={PANEL_HEIGHT}
                      fill={fill}
                      shapeRendering="crispEdges"
                    />
                  ))}
                </g>
              );
            })}

            {buckets.slice(1).map((bucket) => {
              const x = scaleX(bucket.startTime);
              return (
                <g key={`divider-${bucket.key}`}>
                  {channels.map((_, i) => {
                    const panelTop = MARGIN.top + i * (PANEL_HEIGHT + PANEL_GAP);
                    return (
                      <line
                        key={`divider-${bucket.key}-${i}`}
                        x1={x}
                        x2={x}
                        y1={panelTop}
                        y2={panelTop + PANEL_HEIGHT}
                        stroke="var(--mantine-color-default-border)"
                        strokeWidth={1}
                        strokeDasharray="2 4"
                        shapeRendering="crispEdges"
                      />
                    );
                  })}
                </g>
              );
            })}

            {buckets.map((bucket) => {
              const x1 = scaleX(bucket.startTime);
              const x2 = scaleX(bucket.endTime);
              const cx = (x1 + x2) / 2;
              const pillWidth = Math.min(x2 - x1 - 6, 80);
              if (pillWidth < 20) return null;
              const { hue, shade } = stagePillStyles(bucket);
              return (
                <g key={`pill-${bucket.key}`} transform={`translate(${cx}, ${MARGIN.top / 2 + 2})`}>
                  <rect
                    x={-pillWidth / 2}
                    y={-9}
                    width={pillWidth}
                    height={18}
                    rx={9}
                    ry={9}
                    fill={`var(--mantine-color-${hue}-0)`}
                    stroke={`var(--mantine-color-${hue}-${shade})`}
                    strokeWidth={1}
                  />
                  <text
                    x={0}
                    y={3}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight={700}
                    fill={`var(--mantine-color-${hue}-8)`}
                  >
                    {bucket.shortLabel}
                  </text>
                </g>
              );
            })}

            {channels.map((ch, i) => {
              const panelTop = MARGIN.top + i * (PANEL_HEIGHT + PANEL_GAP);
              const panelBottom = panelTop + PANEL_HEIGHT;
              return (
                <g key={ch.key}>
                  {i > 0 && (
                    <line
                      x1={MARGIN.left}
                      x2={MARGIN.left + plotWidth}
                      y1={panelTop}
                      y2={panelTop}
                      stroke="var(--mantine-color-default-border)"
                      strokeWidth={1}
                      shapeRendering="crispEdges"
                    />
                  )}

                  {ch.yTickValues.map((tv, j) => {
                    const y = scaleYForChannel(i, tv);
                    const isEdge = j === 0 || j === ch.yTickValues.length - 1;
                    return (
                      <g key={`yt-${ch.key}-${j}`}>
                        <line
                          x1={MARGIN.left}
                          x2={MARGIN.left + plotWidth}
                          y1={y}
                          y2={y}
                          stroke="var(--mantine-color-default-border)"
                          strokeWidth={1}
                          strokeDasharray={isEdge ? undefined : "2 4"}
                          shapeRendering="crispEdges"
                        />
                        <text
                          x={MARGIN.left - 6}
                          y={y}
                          textAnchor="end"
                          dominantBaseline="middle"
                          fontSize={10}
                          fill="var(--mantine-color-dimmed)"
                        >
                          {ch.yFormat(tv)}
                        </text>
                      </g>
                    );
                  })}

                  <g
                    transform={`translate(${MARGIN.left - 50}, ${
                      (panelTop + panelBottom) / 2
                    }) rotate(-90)`}
                  >
                    <text
                      textAnchor="middle"
                      fontSize={11}
                      fontWeight={600}
                      fill="var(--mantine-color-text)"
                    >
                      {ch.label}
                    </text>
                    <text
                      y={14}
                      textAnchor="middle"
                      fontSize={10}
                      fill="var(--mantine-color-dimmed)"
                    >
                      ({ch.unit})
                    </text>
                  </g>

                  {ch.noDataKey ? (
                    <text
                      x={MARGIN.left + plotWidth / 2}
                      y={(panelTop + panelBottom) / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={11}
                      fill="var(--mantine-color-dimmed)"
                    >
                      {t(ch.noDataKey)}
                    </text>
                  ) : (
                    <>
                      {bandPaths[i] ? (
                        <path
                          d={bandPaths[i] as string}
                          fill={`var(--mantine-color-${ch.bandColor ?? ch.primaryColor ?? LINE_COLOR})`}
                          fillOpacity={BAND_FILL_OPACITY}
                          stroke="none"
                        />
                      ) : null}

                      <polyline
                        points={polylines[i]}
                        fill="none"
                        stroke={`var(--mantine-color-${ch.primaryColor ?? LINE_COLOR})`}
                        strokeWidth={LINE_STROKE_WIDTH}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />

                      {secondaryPolylines[i] ? (
                        <polyline
                          points={secondaryPolylines[i] as string}
                          fill="none"
                          stroke={`var(--mantine-color-${ch.secondaryColor ?? LINE_COLOR})`}
                          strokeWidth={LINE_STROKE_WIDTH}
                          strokeLinejoin="round"
                          strokeLinecap="round"
                        />
                      ) : null}
                    </>
                  )}
                </g>
              );
            })}

            <g transform={`translate(0, ${MARGIN.top + channelStackHeight})`}>
              <line
                x1={MARGIN.left}
                x2={MARGIN.left + plotWidth}
                y1={0}
                y2={0}
                stroke="var(--mantine-color-dimmed)"
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
              {xTicks.map((tick) => {
                const isLabeled = xLabels.has(tick);
                return (
                  <g key={`xt-${tick}`} transform={`translate(${scaleX(tick)}, 0)`}>
                    <line
                      x1={0}
                      x2={0}
                      y1={0}
                      y2={isLabeled ? 5 : 3}
                      stroke="var(--mantine-color-dimmed)"
                      strokeWidth={1}
                    />
                    {isLabeled ? (
                      <text
                        x={0}
                        y={18}
                        textAnchor="middle"
                        fontSize={10}
                        fill="var(--mantine-color-dimmed)"
                      >
                        {Math.round(tick)}
                      </text>
                    ) : null}
                  </g>
                );
              })}
              <text
                x={MARGIN.left + plotWidth / 2}
                y={32}
                textAnchor="middle"
                fontSize={11}
                fontWeight={600}
                fill="var(--mantine-color-text)"
              >
                {t(`${ns}.timeAxisLabel`)}
              </text>
            </g>

            {hover
              ? (() => {
                  const channel = channels[hover.channelIndex];
                  const px = scaleX(hover.point.t);
                  const py = scaleYForChannel(hover.channelIndex, hover.point.v);
                  const dotColor = `var(--mantine-color-${channel.primaryColor ?? LINE_COLOR})`;
                  const boxWidth = 124;
                  const boxHeight = 50;
                  const flip = px + 12 + boxWidth > MARGIN.left + plotWidth;
                  const boxX = flip ? px - 12 - boxWidth : px + 12;
                  const boxY = Math.max(
                    MARGIN.top,
                    Math.min(py - boxHeight / 2, MARGIN.top + channelStackHeight - boxHeight),
                  );
                  const seriesLabel = hover.sublabel ?? channel.label;
                  return (
                    <g pointerEvents="none">
                      <circle
                        cx={px}
                        cy={py}
                        r={3.5}
                        fill={dotColor}
                        stroke="var(--mantine-color-body)"
                        strokeWidth={1.5}
                      />
                      <g transform={`translate(${boxX}, ${boxY})`}>
                        <rect
                          width={boxWidth}
                          height={boxHeight}
                          rx={6}
                          ry={6}
                          fill="var(--mantine-color-body)"
                          stroke="var(--mantine-color-default-border)"
                          strokeWidth={1}
                        />
                        <text
                          x={10}
                          y={16}
                          fontSize={10}
                          fontWeight={700}
                          fill="var(--mantine-color-text)"
                        >
                          {seriesLabel}
                        </text>
                        <text x={10} y={30} fontSize={10} fill="var(--mantine-color-text)">
                          {t(`${ns}.tooltip.reading`, {
                            value: channel.yFormat(hover.point.v),
                            unit: channel.unit,
                          })}
                        </text>
                        <text x={10} y={43} fontSize={10} fill="var(--mantine-color-dimmed)">
                          {t(`${ns}.tooltip.time`, { time: hover.point.t.toFixed(1) })}
                        </text>
                      </g>
                    </g>
                  );
                })()
              : null}
          </svg>
        </Box>

        <Group gap="lg" wrap="wrap">
          <LegendEntry
            color={`var(--mantine-color-${LINE_COLOR})`}
            label={t(`${ns}.legend.value`)}
          />
          <LegendEntry color="var(--mantine-color-orange-6)" label={t(`${ns}.legend.pupilLeft`)} />
          <LegendEntry color="var(--mantine-color-blue-6)" label={t(`${ns}.legend.pupilRight`)} />
          <LegendEntry
            color="var(--mantine-color-gray-5)"
            opacity={BAND_FILL_OPACITY}
            label={t(`${ns}.legend.pupilRange`)}
            isBand
          />
          <LegendEntry
            color="var(--mantine-color-gray-5)"
            label={t(`${ns}.legend.baseline`)}
            rounded
          />
          <LegendEntry
            color="var(--mantine-color-indigo-5)"
            label={t(`${ns}.legend.visual`)}
            rounded
          />
          <LegendEntry
            color="var(--mantine-color-teal-5)"
            label={t(`${ns}.legend.auditory`)}
            rounded
          />
          <LegendEntry
            color="var(--mantine-color-violet-5)"
            label={t(`${ns}.legend.combined`)}
            rounded
          />
        </Group>
      </Stack>
    </Card>
  );
};

const LegendEntry: React.FC<{
  color: string;
  label: string;
  opacity?: number;
  isBand?: boolean;
  rounded?: boolean;
}> = ({ color, label, opacity = 1, isBand, rounded }) => (
  <Group gap={6} wrap="nowrap">
    <Box
      style={{
        width: isBand ? 18 : 14,
        height: isBand ? 8 : rounded ? 10 : 3,
        borderRadius: rounded ? 5 : 2,
        backgroundColor: color,
        opacity,
      }}
    />
    <Text size="xs" c="dimmed">
      {label}
    </Text>
  </Group>
);

export default CPTEyeTrackingPanel;
